import { useMemo } from 'react';
import type { ConversationItem } from '@vivport/backend/types';
import { Folder } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { DriveFileRow } from '@/components/drives/DriveFileRow';
import { resolveKeptRef } from '@/components/keep/kept-ref';
import { useDriveMirroredRefs } from '@/hooks/useDrivesQueries';

type DriveEntryListItem = Extract<ConversationItem, { kind: 'drive_entry_list' }>;

export interface ProvenanceDriveEntryListCardProps {
  item: DriveEntryListItem;
}

export function ProvenanceDriveEntryListCard({ item }: ProvenanceDriveEntryListCardProps) {
  const paths = useMemo(() => item.files.map((file) => file.path), [item.files]);
  const refsQuery = useDriveMirroredRefs(item.driveId, paths);
  const fileCount = item.files.length;
  const title =
    item.listRole === 'FOLDER'
      ? `${item.path ?? '/'} on ${item.driveName}`
      : `${fileCount} file${fileCount === 1 ? '' : 's'} for '${item.query ?? ''}' on ${item.driveName}`;
  const suffix = item.listRole === 'FIND' && item.limitReached ? ' · limit reached' : null;
  const empty = item.listRole === 'FOLDER' && item.folders.length === 0 && item.files.length === 0;

  return (
    <div className="w-full rounded-lg border border-border p-3 text-sm text-foreground">
      <div className="flex items-center gap-2">
        <Folder className="h-4 w-4 shrink-0" />
        <p className="min-w-0 truncate font-medium">
          {title}
          {suffix ? <span className="font-normal text-xs text-muted-foreground">{suffix}</span> : null}
        </p>
        <Badge variant="outline" className="ml-auto shrink-0">
          {item.productLabel}
        </Badge>
      </div>
      {empty ? <p className="mt-2 text-muted-foreground">Empty folder</p> : null}
      {item.folders.length > 0 ? (
        <>
          {item.listRole === 'FIND' ? <p className="mt-2 text-xs font-medium text-muted-foreground">Folders</p> : null}
          <ul className="mt-2 divide-y divide-border">
            {item.folders.map((folder) => (
              <li key={folder.path} className="flex items-center gap-2 py-2">
                <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 truncate">{folder.name}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {item.files.length > 0 ? (
        <ul className="mt-2 divide-y divide-border">
          {item.files.map((file) => (
            <DriveFileRow
              key={file.path}
              file={{
                name: file.name,
                path: file.path,
                sizeBytes: file.sizeBytes,
                mimeType: file.mimeType,
                modifiedAt: file.modifiedAt,
                webUrl: file.webUrl,
                mirroredContentId: file.mirroredContentId,
                mirroredCode: file.mirroredCode,
              }}
              driveId={item.driveId}
              productLabel={item.productLabel}
              kept={resolveKeptRef(refsQuery.data, file.path, file.mirroredContentId, file.mirroredCode)}
            />
          ))}
        </ul>
      ) : null}
    </div>
  );
}
